import type { AnalyticsEventData } from './analytics-types';
import type { Lead } from '@/app/leads/types';

/**
 * Event name constants and payload conventions.
 *
 * Base payload (added by useAnalytics from profile): institutionId, userId, institutionName.
 * Callers add event-specific data only.
 *
 * Payload by event:
 *
 * - Dashboard Panel - Dashboard Viewed
 *   data: {}
 *
 * - Dashboard Panel - Date Filter Changed
 *   data: { filters: { timeframe: string } }
 *
 * - Dashboard Panel - Audience Filter Changed
 *   data: { filters: { audience: "all" | "international" | "domestic" } }
 *
 * - Dashboard Panel - Dashboard Filter changed
 *   data: { filterType: "timeframe" | "citizenship", filterValue: string }
 *
 * - Dashboard Panel - Leads List - Viewed
 *   data: { tab?: LeadsListTab }
 *
 * - Dashboard Panel - Leads List - Tab switched
 *   data: { tabFrom: LeadsListTab, tabTo: LeadsListTab }
 *
 * - Dashboard Panel - Leads List - Filters applied
 *   data: { filters: { status?, citizenship?, lastConversation?, engagementLevel? } }
 *
 * - Dashboard Panel - Export - Button clicked
 *   data: { context: ExportContext }
 *
 * - Dashboard Panel - Export - Export button clicked
 *   data: { context: 'list' }
 *
 * - Dashboard Panel - Export - Export type selected
 *   data: { exportType: 'active' | 'archived' }
 *
 * - Dashboard Panel - Export - Export Automatic Archive Selected
 *   data: { archiveAfterExport: boolean, exportType: 'active' }
 *
 * - Dashboard Panel - Export - Export confirmed
 *   data: {
 *     exportType: 'active' | 'archived' | 'leads' | 'visitors', // 'leads' and 'visitors' map to 'active' for legacy compatibility
 *     dateRangePreset: 'last7days' | 'last30days' | 'custom',
 *     archiveAfterExport?: boolean,
 *     leadCount?: number,
 *     customDateFrom?: string,
 *     customDateTo?: string,
 *   }
 *
 * - Knowledge Hub - Sources - Viewed
 *   data: {}
 *
 * - Knowledge Hub - Sources - Filters applied
 *   data: { filterType: string, filterValue: string }
 *
 * - Knowledge Hub - Sources - Add source clicked
 *   data: {}
 *
 * - Knowledge Hub - Sources - Source deleted
 *   data: { sourceId: string | number, sourceType: string }
 *
 * - Knowledge Hub - Sources - Source downloaded
 *   data: { sourceId: string | number }
 *
 * - Knowledge Hub - Sources - URL list download clicked
 *   data: { sourceId, totalUrlCount, includedUrlCount, excludedUrlCount, generationMode: 'sync' | 'async' }
 *
 * - Knowledge Hub - Sources - URL list download completed
 *   data: { sourceId, totalUrlCount, generationDurationMs, generationMode }
 *
 * - Knowledge Hub - Sources - URL list download failed
 *   data: { sourceId, errorType: 'timeout' | 'server_error' | 'network' | 'unknown', generationMode }
 *
 * - Knowledge Hub - Sources - Source Added
 *   data: { sourceType: 'url' | 'document' | 'text' }
 *
 * - Knowledge Hub - Sources - External Source Selected
 *   data: { isExternal: boolean }
 *
 * - Knowledge Hub - Sources - Audience Selected
 *   data: { action: 'select_all' | 'deselect_all' } | { audienceType: string, action: 'selected' | 'deselected' }
 *
 * - Exclusion Manager Viewed (SEM-6923; shared across Knowledge Hub + Admin RAG)
 *   data: {
 *     scope: 'institution_wide' | 'per_source',
 *     entryPoint: 'institution_manager' | 'source_detail',
 *     matchType: 'keyword' | 'exact_url',
 *     presetUsed: string,
 *     userRole: string,
 *     ragSystemId?: string,
 *   }
 *   institutionId auto-merged from profile
 *
 * - Exclusion Rule Preview Run
 *   data: { ...base exclusion fields, previewIteration, pagesExcludedCount, pagesRemainingCount, sourcesAffectedCount }
 *
 * - Exclusion Rule Saved
 *   data: { ...base exclusion fields, pagesExcludedCount?, pagesRemainingCount?, sourcesAffectedCount?, rulesSavedCount }
 *
 * - Exclusion Rule Deleted
 *   data: { ...base exclusion fields, ruleAgeDays }
 *
 * - Exclusion Rule Action Failed
 *   data: { ...base exclusion fields, stage: 'preview' | 'save' | 'delete', errorReason: string }
 *
 * - Auth - Login - Page Viewed
 *   data: { error?: string }
 *
 * - Auth - Login - Microsoft Sign In Clicked
 *   data: {}
 *
 * - Auth - Login - Google Sign In Clicked
 *   data: {}
 *
 * - Profile - Profile Viewed
 *   data: {}
 *
 * - Profile - Sign Out Clicked
 *   data: {}
 *
 * - Profile - Translation Language Changed
 *   data: { fromLanguage: string, toLanguage: string }
 *
 * - Settings - Institution - Viewed
 *   data: {}
 *
 * - Settings - Institution - Settings Updated
 *   data: { section: 'basicInfo' | 'location' | 'policy' }
 *
 * - Settings - Appearance - Viewed (settings UI: "AI Chat Appearance"; event titles unchanged)
 *   data: {}
 *
 * - Settings - Appearance - Avatar Uploaded
 *   data: {}
 *
 * - Settings - Appearance - Avatar Removed
 *   data: {}
 *
 * - Settings - Appearance - Primary Color Changed
 *   data: { color: string }
 *
 * - Settings - Appearance - Secondary Color Changed
 *   data: { color: string }
 *
 * - Settings - Appearance - Desktop Variation Selected
 *   data: { variation: string }
 *
 * - Settings - Appearance - Quick Start Placement Selected
 *   data: { placement: 'within_chat' | 'on_hover' }
 *
 * - Settings - Appearance - Quick Start Frequency Selected
 *   data: { frequency: 'every_new_chat' | 'until_first_message' }
 *
 * - Settings - Appearance - Font Size Selected
 *   data: { size: string }
 *
 * - Settings - Appearance - Quick Start Button Added
 *   data: { promptId: string }
 *
 * - Settings - Appearance - Quick Start Button Updated
 *   data: { promptId: string }
 *
 * - Settings - Appearance - Quick Start Button Visibility Toggled
 *   data: { promptId: string, isVisible: boolean }
 *
 * - Settings - Appearance - Quick Start Button Deleted
 *   data: { promptId: string }
 *
 * - Settings - Appearance - Footer Text Changed
 *   data: {}
 *
 * - Chat Height Changed
 *   data: { previous_value: 'full' | 'compact', new_value: 'full' | 'compact' }
 *
 * - Appearance - Settings Saved
 *   data: { chat_height: 'full' | 'compact' }
 *
 * - Settings - Appearance - Session Timeout Changed
 *   data: { previousValue: number | null, newValue: number | null, isCustom: boolean, unit: 'minutes' | 'hours' | 'days' | null }
 *   previousValue and newValue are minutes. null means Never.
 *
 * - Settings - Appearance - Session Timeout Custom Error
 *   data: { errorType: 'empty' | 'not_whole' | 'below_min' | 'above_max', unit: 'minutes' | 'hours' | 'days' }
 *
 * - Settings - Users & Roles - Viewed
 *   data: {}
 *
 * - Settings - Users & Roles - Add User Modal Opened
 *   data: {}
 *
 * - Settings - Users & Roles - User Added
 *   data: { role: string }
 *
 * - Settings - Users & Roles - User Updated
 *   data: { userId: string }
 *
 * - Settings - Users & Roles - User Deactivated
 *   data: { userId: string }
 *
 * - Settings - Users & Roles - User Reactivated
 *   data: { userId: string }
 *
 * - Settings - Agent Setup - Embed Code Copied
 *   data: {}
 *
 * - Settings - Agent Setup - WhatsApp Number Copied
 *   data: {}
 *
 * - Settings - Integrations - API Key Generate Dialog Opened
 *   data: {}
 *
 * - Settings - Integrations - API Key Created
 *   data: { nameLength: number }
 *
 * - Settings - Integrations - API Key Generation Failed
 *   data: { errorType: 'timeout' | 'server_error' | 'network' | 'unknown' }
 *
 * - Settings - Integrations - API Key Copied
 *   data: {}
 *
 * - Settings - Integrations - API Key Reveal Dismissed
 *   data: { wasCopied: boolean }
 *
 * - Settings - Integrations - API Key Revoked
 *   data: { ageDays: number, wasEverUsed: boolean }
 *
 * - Settings - Tab Switched
 *   data: { tab: 'institution' | 'agent' | 'appearance' | 'users' }
 *
 * - Dashboard Panel - Lead Detail - Convert to Lead Clicked
 *   data: { leadId: string }
 *
 * - Dashboard Panel - Lead Detail - Convert to Lead Confirmed
 *   data: { leadId: string, ...buildLeadMetadata(lead) }
 *
 * - Admin - Institutions - Viewed
 *   data: {}
 *
 * - Admin - Institutions - Add Institution Modal Opened
 *   data: {}
 *
 * - Admin - Institutions - Institution Added
 *   data: { institutionCode: string }
 *
 * - Admin - Institutions - Switch Clicked
 *   data: { institutionId: string, institutionName: string }
 *
 * - Admin - Institutions - Switched
 *   data: { institutionId: string, institutionName: string }
 *
 * - Admin - Institution Detail - Viewed
 *   data: { institutionId: string, institutionName: string }
 *
 * - Admin - Web Scraper - Viewed
 *   data: {}
 *
 * - Admin - Web Scraper - Refresh Clicked
 *   data: {}
 *
 * - Admin - Web Scraper - Institution Card Expanded
 *   data: { institutionId: string, institutionName: string }
 *
 * - Admin - Web Scraper - Website Created
 *   data: { institutionId: string, institutionName: string }
 *
 * - Admin - Web Scraper - Website Updated
 *   data: { institutionId: string, institutionName: string, websiteId: string }
 *
 * - Admin - Web Scraper - Website Deleted
 *   data: { institutionId: string, institutionName: string, websiteId: string }
 *
 * - Admin - Web Scraper - Crawl Started
 *   data: { institutionId: string, institutionName: string, websiteId: string, forceFullCrawl: boolean }
 *
 * - Admin - RAG Config - Viewed
 *   data: {}
 *
 * - Admin - RAG Config - Refresh Clicked
 *   data: {}
 *
 * - Admin - RAG Config - Embedding Provider Toggled
 *   data: { switchToEmbeddingFallbackProvider: boolean }
 *
 * - Admin - RAG Systems - Delete Initiated
 *   data: { ragSystemId: string, websitesCount: number, documentsCount: number }
 *
 * - Admin - RAG Systems - Delete Confirmed
 *   data: { ragSystemId: string }
 *
 * - Admin - RAG Systems - Delete Failed
 *   data: { ragSystemId: string, errorMessage: string }
 *
 * - Admin - RAG Systems - Restored
 *   data: { ragSystemId: string }
 *
 * - Admin - Users & Roles - Viewed
 *   data: {}
 *
 * - Admin - Users & Roles - Add User Modal Opened
 *   data: {}
 *
 * - Admin - Users & Roles - User Added
 *   data: { platformRole: 'admin' | 'staff' }
 *
 * - Admin - Users & Roles - User Updated
 *   data: { userId: string }
 *
 * - Admin - Users & Roles - User Deactivated
 *   data: { userId: string }
 *
 * - Admin - Users & Roles - User Reactivated
 *   data: { userId: string }
 *
 * - Admin - Users & Roles - Institution Access Opened
 *   data: { userId: string }
 *
 * - Admin - Users & Roles - Institution Access Saved
 *   data: { userId: string, addedCount: number, removedCount: number }
 *
 * - Admin - Tab Switched
 *   data: { tab: 'institutions' | 'users' | 'rag-systems' | 'rag-configuration' | 'rag' }
 *   Top-level tab IDs are 'institutions', 'users', 'rag-systems'.
 *   Sub-tab IDs ('rag', 'rag-configuration') fire when the corresponding
 *   child item is clicked under an expandable parent.
 *
 * - Engage Copilot - Opened
 *   data: {}
 *
 * - Engage Copilot - Closed
 *   data: {}
 *
 * - Engage Copilot - Message Sent
 *   data: { messageLength: number }
 *
 * - Navigation - Sidebar - Link Clicked
 *   data: { destination: string }
 */

// Dashboard
export const DASHBOARD_VIEWED = 'Dashboard Panel - Dashboard Viewed';
export const DASHBOARD_DATE_FILTER_CHANGED =
  'Dashboard Panel - Date Filter Changed';
export const DASHBOARD_AUDIENCE_FILTER_CHANGED =
  'Dashboard Panel - Audience Filter Changed';
export const DASHBOARD_FILTER_CHANGED =
  'Dashboard Panel - Dashboard Filter changed';

// Leads List
export const LEADS_LIST_VIEWED = 'Dashboard Panel - Leads List - Viewed';
export const LEADS_LIST_TAB_SWITCHED =
  'Dashboard Panel - Leads List - Tab switched';
export const LEADS_LIST_FILTERS_APPLIED =
  'Dashboard Panel - Leads List - Filters applied';
export const LEADS_LIST_SEARCH_USED =
  'Dashboard Panel - Leads List - Search Used';

// Lead Export (bulk CSV modal)
export const LEAD_EXPORT_BUTTON_CLICKED =
  'Dashboard Panel - Export - Export button clicked';
export const LEAD_EXPORT_ARCHIVE_AFTER_SELECTED =
  'Dashboard Panel - Export - Export Automatic Archive Selected';
export const LEAD_EXPORT_CONFIRMED =
  'Dashboard Panel - Export - Export confirmed';

// Lead Detail
export const LEAD_DETAIL_LEAD_VIEWED =
  'Dashboard Panel - Lead Detail - Lead Viewed';
export const LEAD_DETAIL_CONVERSATIONS_TOGGLED =
  'Dashboard Panel - Lead Detail - Conversations Toggled';
export const LEAD_DETAIL_AI_SUMMARY_TOGGLED =
  'Dashboard Panel - Lead Detail - AI Conversation Summary Toggled';
export const LEAD_DETAIL_TRANSLATE_TOGGLE_USED =
  'Dashboard Panel - Lead Detail - Translate Toggle Used';
export const LEAD_DETAIL_PROFILE_SECTION_EDITED =
  'Dashboard Panel - Lead Detail - Profile Section Edited';
export const LEAD_DETAIL_STATUS_UPDATED =
  'Dashboard Panel - Lead Detail - Lead Status Updated';
export const LEAD_DETAIL_LEAD_ARCHIVED =
  'Dashboard Panel - Lead Detail - Lead Archived';
export const LEAD_DETAIL_LEAD_UNARCHIVED =
  'Dashboard Panel - Lead Detail - Lead Unarchived';
export const LEAD_DETAIL_ACTIVITY_NOTE_ADDED =
  'Dashboard Panel - Lead Detail - Activity - Note Added';
export const LEAD_DETAIL_ACTIVITY_CALL_LOGGED =
  'Dashboard Panel - Lead Detail - Activity - Call Logged';
export const LEAD_DETAIL_ACTIVITY_EMAIL_LOGGED =
  'Dashboard Panel - Lead Detail - Activity - Email Logged';
export const LEAD_DETAIL_ACTIVITY_MEETING_LOGGED =
  'Dashboard Panel - Lead Detail - Activity - Meeting Logged';

// Lead Detail — Export & Delete
export const LEAD_DETAIL_EXPORT_DATA_CLICKED =
  'Dashboard Panel - Lead Detail - Export Lead Data Clicked';
export const LEAD_DETAIL_EXPORT_DATA_REASON_SELECTED =
  'Dashboard Panel - Lead Detail - Export Lead Data Reason Selected';
export const LEAD_DETAIL_EXPORT_DATA_CONFIRMED =
  'Dashboard Panel - Lead Detail - Export Lead Data Confirmed';
export const LEAD_DETAIL_DELETE_DATA_CLICKED =
  'Dashboard Panel - Lead Detail - Delete Lead Data Clicked';
export const LEAD_DETAIL_DELETE_DATA_CONFIRMED =
  'Dashboard Panel - Lead Detail - Delete Lead Data Confirmed';

// AI Chat Agent Configuration (Knowledge Sources)
export const AGENT_CONFIG_VIEWED = 'AI Chat Agent Configuration - Viewed';
export const AGENT_CONFIG_ADD_SOURCE_CLICKED =
  'AI Chat Agent Configuration - Add Source Clicked';
export const AGENT_CONFIG_SOURCE_ADDED =
  'AI Chat Agent Configuration - Source Added';
export const AGENT_CONFIG_SOURCE_REMOVED =
  'AI Chat Agent Configuration - Source Removed';
export const AGENT_CONFIG_EXTERNAL_SOURCE_SELECTED =
  'AI Chat Agent Configuration - External Source Selected';
export const AGENT_CONFIG_AUDIENCE_SELECTED =
  'AI Chat Agent Configuration - Audience Selected';

// WhatsApp QR Code
export const WHATSAPP_QR_LINK_COPIED =
  'AI Chat Agent Configuration - WhatsApp QR Link Copied';
export const WHATSAPP_QR_DOWNLOADED =
  'AI Chat Agent Configuration - WhatsApp QR Downloaded';

// WhatsApp Deflection — web chat header link to wa.me
export const SETTINGS_WHATSAPP_DEFLECTION_TOGGLE_ENABLED =
  'AI Chat Agent Configuration - WhatsApp Deflection - Toggle Enabled';
export const SETTINGS_WHATSAPP_DEFLECTION_TOGGLE_DISABLED =
  'AI Chat Agent Configuration - WhatsApp Deflection - Toggle Disabled';
export const CHAT_WIDGET_WHATSAPP_DEFLECTION_CLICKED =
  'Chat Widget - WhatsApp Deflection Clicked';

export const CHAT_WIDGET_PANEL_RENDERED = 'Chat Widget - Panel Rendered';
export const CHAT_WIDGET_PANEL_EXPANDED = 'Chat Widget - Panel Expanded';
export const CHAT_WIDGET_PANEL_COLLAPSED = 'Chat Widget - Panel Collapsed';

/** SEM-7139 — mandatory intake form on widget open */
export const CHAT_WIDGET_INTAKE_FORM_SHOWN = 'Chat Widget - Intake Form Shown';
export const CHAT_WIDGET_INTAKE_FIELD_COMPLETED =
  'Chat Widget - Intake Field Completed';
export const CHAT_WIDGET_INTAKE_COUNTRY_CODE_CHANGED =
  'Chat Widget - Intake Country Code Changed';
export const CHAT_WIDGET_INTAKE_VALIDATION_ERROR =
  'Chat Widget - Intake Validation Error';
export const CHAT_WIDGET_INTAKE_FORM_SUBMITTED =
  'Chat Widget - Intake Form Submitted';
export const CHAT_WIDGET_INTAKE_FORM_ABANDONED =
  'Chat Widget - Intake Form Abandoned';
export const CHAT_WIDGET_INTAKE_NO_LAST_NAME_DECLARED =
  'Chat Widget - No Last Name Declared';

export const CHAT_WIDGET_AVATAR_STREAM_INITIALIZED =
  'Chat Widget - Avatar Stream Initialized';
export const CHAT_WIDGET_AVATAR_STREAM_TERMINATED =
  'Chat Widget - Avatar Stream Terminated';
export const CHAT_WIDGET_AVATAR_SESSION_TIMEDOUT =
  'Chat Widget - Avatar Session Timedout';
export const CHAT_WIDGET_AVATAR_MIC_CLICKED =
  'Chat Widget - Avatar Mic Clicked';
export const CHAT_WIDGET_AVATAR_CONCURRENCY_CAP_HIT =
  'Chat Widget - Avatar Concurrency Cap Hit';
export const CHAT_WIDGET_AVATAR_STREAM_FAILURE =
  'Chat Widget - Avatar Stream Failure';

/**
 * Avatar speech lifecycle — incremental TTS during LLM streaming.
 * - CHAT_WIDGET_AVATAR_SPEECH_STARTED: first chunk plays (`timeToFirstWordMs`, `institutionId`, `sessionId`, `messageId`)
 * - CHAT_WIDGET_AVATAR_SPEECH_STALLED: gap before next chunk exceeds tolerance (`stallDurationMs`, `chunkIndex`, `institutionId`, `sessionId`)
 * - CHAT_WIDGET_AVATAR_SPEECH_COMPLETED: full response spoken (`totalDurationMs`, `chunkCount`, `stallCount`, `institutionId`, `sessionId`)
 */
export const CHAT_WIDGET_AVATAR_SPEECH_STARTED =
  'Chat Widget - Avatar Speech Started';
export const CHAT_WIDGET_AVATAR_SPEECH_STALLED =
  'Chat Widget - Avatar Speech Stalled';
export const CHAT_WIDGET_AVATAR_SPEECH_COMPLETED =
  'Chat Widget - Avatar Speech Completed';

// Transcript download — web chat session export
export const CHAT_TRANSCRIPT_DOWNLOAD_SHOWN =
  'Chat - Transcript Download Shown';
export const CHAT_TRANSCRIPT_DOWNLOAD_CLICKED =
  'Chat - Transcript Download Clicked';
export const CHAT_TRANSCRIPT_DOWNLOAD_SUCCEEDED =
  'Chat - Transcript Download Succeeded';
export const CHAT_TRANSCRIPT_DOWNLOAD_FAILED =
  'Chat - Transcript Download Failed';
export const CHAT_TRANSCRIPT_DOWNLOAD_FALLBACK =
  'Chat - Transcript Download Fallback';
export const CHAT_WIDGET_CONVERSATION_CLEARED =
  'Chat Widget - Conversation Cleared';

/** SEM-7238 — browser language → applied widget locale (once per load) */
export const WEB_CHAT_WIDGET_LANGUAGE_APPLIED =
  'Web Chat - Widget Language Applied';

// Knowledge Hub
export const KNOWLEDGE_HUB_SOURCES_VIEWED = 'Knowledge Hub - Sources - Viewed';
export const KNOWLEDGE_HUB_SOURCES_FILTERS_APPLIED =
  'Knowledge Hub - Sources - Filters applied';
export const KNOWLEDGE_HUB_SOURCES_ADD_SOURCE_CLICKED =
  'Knowledge Hub - Sources - Add source clicked';
export const KNOWLEDGE_HUB_SOURCES_SOURCE_DELETED =
  'Knowledge Hub - Sources - Source deleted';
export const KNOWLEDGE_HUB_SOURCES_SOURCE_DOWNLOADED =
  'Knowledge Hub - Sources - Source downloaded';
export const KNOWLEDGE_HUB_SOURCES_SOURCE_VIEWED =
  'Knowledge Hub - Sources - Source viewed';
export const KNOWLEDGE_HUB_SOURCES_REFRESH_SCHEDULE_CHANGED =
  'Knowledge Hub - Sources - Refresh schedule changed';
// URL-list CSV download (SEM-6405) — institutionId/userId auto-merged from profile
//   ..._URL_LIST_DOWNLOAD_CLICKED: user clicked Download URL list.
//     data: { sourceId, totalUrlCount, includedUrlCount, excludedUrlCount, generationMode: 'sync' | 'async' }
//   ..._URL_LIST_DOWNLOAD_COMPLETED: CSV delivered (sync) or notification opened (async; not shipped yet).
//     data: { sourceId, totalUrlCount, generationDurationMs, generationMode }
//   ..._URL_LIST_DOWNLOAD_FAILED: download failed.
//     data: { sourceId, errorType: 'timeout' | 'server_error' | 'network' | 'unknown', generationMode }
export const KNOWLEDGE_HUB_SOURCES_URL_LIST_DOWNLOAD_CLICKED =
  'Knowledge Hub - Sources - URL list download clicked';
export const KNOWLEDGE_HUB_SOURCES_URL_LIST_DOWNLOAD_COMPLETED =
  'Knowledge Hub - Sources - URL list download completed';
export const KNOWLEDGE_HUB_SOURCES_URL_LIST_DOWNLOAD_FAILED =
  'Knowledge Hub - Sources - URL list download failed';
export const KNOWLEDGE_HUB_SOURCE_ADDED =
  'Knowledge Hub - Sources - Source Added';
export const KNOWLEDGE_HUB_EXTERNAL_SOURCE_SELECTED =
  'Knowledge Hub - Sources - External Source Selected';
export const KNOWLEDGE_HUB_AUDIENCE_SELECTED =
  'Knowledge Hub - Sources - Audience Selected';
export const KNOWLEDGE_HUB_INSIGHTS_VIEWED =
  'Knowledge Hub - Insights - Viewed';
export const KNOWLEDGE_HUB_INSIGHTS_AUDIENCE_FILTER_CHANGED =
  'Knowledge Hub - Insights - Audience filter changed';
export const KNOWLEDGE_HUB_INSIGHTS_TIME_RANGE_CHANGED =
  'Knowledge Hub - Insights - Time range changed';
export const KNOWLEDGE_HUB_INSIGHTS_REFRESH_CLICKED =
  'Knowledge Hub - Insights - Refresh clicked';

// Exclusion management (SEM-6923 — shared event names for Knowledge Hub + Admin RAG)
export const EXCLUSION_MANAGER_VIEWED = 'Exclusion Manager Viewed';
export const EXCLUSION_RULE_PREVIEW_RUN = 'Exclusion Rule Preview Run';
export const EXCLUSION_RULE_SAVED = 'Exclusion Rule Saved';
export const EXCLUSION_RULE_DELETED = 'Exclusion Rule Deleted';
export const EXCLUSION_RULE_ACTION_FAILED = 'Exclusion Rule Action Failed';

// Auth
export const AUTH_LOGIN_PAGE_VIEWED = 'Auth - Login - Page Viewed';
export const AUTH_LOGIN_MICROSOFT_SIGN_IN_CLICKED =
  'Auth - Login - Microsoft Sign In Clicked';
export const AUTH_LOGIN_GOOGLE_SIGN_IN_CLICKED =
  'Auth - Login - Google Sign In Clicked';

// Profile
export const PROFILE_VIEWED = 'Profile - Profile Viewed';
export const PROFILE_SIGN_OUT_CLICKED = 'Profile - Sign Out Clicked';
export const PROFILE_TRANSLATION_LANGUAGE_CHANGED =
  'Profile - Translation Language Changed';

// Settings — Institution
export const SETTINGS_INSTITUTION_VIEWED = 'Settings - Institution - Viewed';
export const SETTINGS_INSTITUTION_UPDATED =
  'Settings - Institution - Settings Updated';

// Settings — Appearance (nav label: AI Chat Appearance)
export const SETTINGS_APPEARANCE_VIEWED = 'Settings - Appearance - Viewed';
export const SETTINGS_APPEARANCE_AVATAR_UPLOADED =
  'Settings - Appearance - Avatar Uploaded';
export const SETTINGS_APPEARANCE_AVATAR_REMOVED =
  'Settings - Appearance - Avatar Removed';
export const SETTINGS_APPEARANCE_PRIMARY_COLOR_CHANGED =
  'Settings - Appearance - Primary Color Changed';
export const SETTINGS_APPEARANCE_SECONDARY_COLOR_CHANGED =
  'Settings - Appearance - Secondary Color Changed';
export const SETTINGS_APPEARANCE_WIDGET_LAUNCHER_STYLE_SELECTED =
  'Settings - Appearance - Widget Launcher Style Selected';
export const SETTINGS_APPEARANCE_QUICK_START_PLACEMENT_SELECTED =
  'Settings - Appearance - Quick Start Placement Selected';
export const SETTINGS_APPEARANCE_QUICK_START_FREQUENCY_SELECTED =
  'Settings - Appearance - Quick Start Frequency Selected';
export const SETTINGS_APPEARANCE_FONT_SIZE_SELECTED =
  'Settings - Appearance - Font Size Selected';
export const SETTINGS_APPEARANCE_QUICK_START_BUTTON_ADDED =
  'Settings - Appearance - Quick Start Button Added';
export const SETTINGS_APPEARANCE_QUICK_START_BUTTON_UPDATED =
  'Settings - Appearance - Quick Start Button Updated';
export const SETTINGS_APPEARANCE_QUICK_START_BUTTON_VISIBILITY_TOGGLED =
  'Settings - Appearance - Quick Start Button Visibility Toggled';
export const SETTINGS_APPEARANCE_QUICK_START_BUTTON_DELETED =
  'Settings - Appearance - Quick Start Button Deleted';
export const SETTINGS_APPEARANCE_FOOTER_TEXT_CHANGED =
  'Settings - Appearance - Footer Text Changed';
export const SETTINGS_APPEARANCE_QUICK_START_INTRO_CHANGED =
  'Settings - Appearance - Quick Start Intro Changed';
export const SETTINGS_APPEARANCE_CHAT_HEIGHT_CHANGED = 'Chat Height Changed';
export const SETTINGS_APPEARANCE_SETTINGS_SAVED = 'Appearance - Settings Saved';
export const SETTINGS_APPEARANCE_SESSION_TIMEOUT_CHANGED =
  'Settings - Appearance - Session Timeout Changed';
export const SETTINGS_APPEARANCE_SESSION_TIMEOUT_CUSTOM_ERROR =
  'Settings - Appearance - Session Timeout Custom Error';

// Settings — Users & Roles
export const SETTINGS_USERS_VIEWED = 'Settings - Users & Roles - Viewed';
export const SETTINGS_USERS_ADD_USER_MODAL_OPENED =
  'Settings - Users & Roles - Add User Modal Opened';
export const SETTINGS_USERS_USER_ADDED =
  'Settings - Users & Roles - User Added';
export const SETTINGS_USERS_USER_UPDATED =
  'Settings - Users & Roles - User Updated';
export const SETTINGS_USERS_USER_DEACTIVATED =
  'Settings - Users & Roles - User Deactivated';
export const SETTINGS_USERS_USER_REACTIVATED =
  'Settings - Users & Roles - User Reactivated';

// Settings — Agent Setup
export const SETTINGS_AGENT_SETUP_EMBED_CODE_COPIED =
  'Settings - Agent Setup - Embed Code Copied';
export const SETTINGS_AGENT_SETUP_WHATSAPP_NUMBER_COPIED =
  'Settings - Agent Setup - WhatsApp Number Copied';

// Settings — Integrations (API keys)
export const SETTINGS_INTEGRATIONS_API_KEY_GENERATE_DIALOG_OPENED =
  'Settings - Integrations - API Key Generate Dialog Opened';
export const SETTINGS_INTEGRATIONS_API_KEY_CREATED =
  'Settings - Integrations - API Key Created';
export const SETTINGS_INTEGRATIONS_API_KEY_GENERATION_FAILED =
  'Settings - Integrations - API Key Generation Failed';
export const SETTINGS_INTEGRATIONS_API_KEY_COPIED =
  'Settings - Integrations - API Key Copied';
export const SETTINGS_INTEGRATIONS_API_KEY_REVEAL_DISMISSED =
  'Settings - Integrations - API Key Reveal Dismissed';
export const SETTINGS_INTEGRATIONS_API_KEY_REVOKED =
  'Settings - Integrations - API Key Revoked';

// Settings — Navigation
export const SETTINGS_TAB_SWITCHED = 'Settings - Tab Switched';

// Integrations — Webhooks
export const INTEGRATIONS_SEND_RULE_CHANGED =
  'Integrations - Send Rule Changed';

// Lead Detail — Convert to Lead
export const LEAD_DETAIL_CONVERT_TO_LEAD_CLICKED =
  'Dashboard Panel - Lead Detail - Convert to Lead Clicked';
export const LEAD_DETAIL_CONVERT_TO_LEAD_CONFIRMED =
  'Dashboard Panel - Lead Detail - Convert to Lead Confirmed';

// Admin — Institutions
export const ADMIN_INSTITUTIONS_VIEWED = 'Admin - Institutions - Viewed';
export const ADMIN_INSTITUTIONS_ADD_MODAL_OPENED =
  'Admin - Institutions - Add Institution Modal Opened';
export const ADMIN_INSTITUTIONS_INSTITUTION_ADDED =
  'Admin - Institutions - Institution Added';
export const ADMIN_INSTITUTIONS_SWITCH_CLICKED =
  'Admin - Institutions - Switch Clicked';
export const ADMIN_INSTITUTIONS_SWITCHED = 'Admin - Institutions - Switched';
export const ADMIN_INSTITUTION_DETAIL_VIEWED =
  'Admin - Institution Detail - Viewed';

// Admin — RAG Config (embedding provider + backfill observability)
export const ADMIN_RAG_CONFIG_VIEWED = 'Admin - RAG Config - Viewed';
export const ADMIN_RAG_CONFIG_REFRESH_CLICKED =
  'Admin - RAG Config - Refresh Clicked';
export const ADMIN_RAG_CONFIG_EMBEDDING_PROVIDER_TOGGLED =
  'Admin - RAG Config - Embedding Provider Toggled';

// Admin — RAG Systems (soft-delete / restore lifecycle)
export const ADMIN_RAG_SYSTEMS_DELETE_INITIATED =
  'Admin - RAG Systems - Delete Initiated';
export const ADMIN_RAG_SYSTEMS_DELETE_CONFIRMED =
  'Admin - RAG Systems - Delete Confirmed';
export const ADMIN_RAG_SYSTEMS_DELETE_FAILED =
  'Admin - RAG Systems - Delete Failed';
export const ADMIN_RAG_SYSTEMS_RESTORED = 'Admin - RAG Systems - Restored';
export const ADMIN_RAG_SYSTEMS_SWITCHER_CHANGED =
  'Admin - RAG Systems - Switcher Changed';

// Admin — Users & Roles (Capio team account management)
export const ADMIN_USERS_VIEWED = 'Admin - Users & Roles - Viewed';
export const ADMIN_USERS_ADD_USER_MODAL_OPENED =
  'Admin - Users & Roles - Add User Modal Opened';
export const ADMIN_USERS_USER_ADDED = 'Admin - Users & Roles - User Added';
export const ADMIN_USERS_USER_UPDATED = 'Admin - Users & Roles - User Updated';
export const ADMIN_USERS_USER_DEACTIVATED =
  'Admin - Users & Roles - User Deactivated';
export const ADMIN_USERS_USER_REACTIVATED =
  'Admin - Users & Roles - User Reactivated';
export const ADMIN_USERS_INSTITUTION_ACCESS_OPENED =
  'Admin - Users & Roles - Institution Access Opened';
export const ADMIN_USERS_INSTITUTION_ACCESS_SAVED =
  'Admin - Users & Roles - Institution Access Saved';

// Admin — Navigation
export const ADMIN_TAB_SWITCHED = 'Admin - Tab Switched';

/**
 * Allowed values for the `tab` property of the `Admin - Tab Switched` event.
 * Keep this union in sync with the IDs declared in `AdminNavigation.tsx`.
 */
export type AdminNavigationTabId =
  | 'institutions'
  | 'users'
  | 'rag-systems'
  | 'rag-configuration'
  | 'rag'
  | 'eval';

// Engage Copilot
export const ENGAGE_COPILOT_OPENED = 'Engage Copilot - Opened';
export const ENGAGE_COPILOT_CLOSED = 'Engage Copilot - Closed';
export const ENGAGE_COPILOT_MESSAGE_SENT = 'Engage Copilot - Message Sent';

// Navigation / Sidebar
export const NAVIGATION_SIDEBAR_LINK_CLICKED =
  'Navigation - Sidebar - Link Clicked';

/** Leads list tab/view mode (Leads | Visitors | Archive) */
export type LeadsListTab = 'leads' | 'visitors' | 'archive';

/** Export context: list = bulk export from list bar, lead_detail = export from lead detail modal */
export type ExportContext = 'list' | 'lead_detail';

/** Lead Export (bulk CSV): active or archived leads (legacy type for analytics compatibility) */
export type LeadExportType = 'active' | 'archived';

/** Lead Export (bulk CSV): leads, visitors, or archived (new type used in UI) */
export type LeadExportTypeNew = 'leads' | 'visitors' | 'archived';

/**
 * Maps the new LeadExportTypeNew to the legacy LeadExportType for analytics compatibility.
 * This ensures backward compatibility with existing analytics dashboards.
 */
export function mapLeadExportTypeToLegacy(
  type: LeadExportTypeNew
): LeadExportType {
  // 'leads' and 'visitors' both map to 'active' for analytics purposes
  // 'archived' maps to 'archived'
  return type === 'archived' ? 'archived' : 'active';
}

/** Lead Export date range preset */
export type LeadExportDateRangePreset = 'last7days' | 'last30days' | 'custom';

/**
 * Minimal profile shape needed to build analytics data (avoids circular deps).
 * institutionName can come from API when included (e.g. relationships.institution.attributes.name).
 */
interface ProfileForAnalytics {
  id: string;
  email: string;
  displayName?: string;
  relationships?: {
    institution?: {
      data: { type: string; id: string } | null;
      attributes?: { name?: string } | null;
    };
  };
}

/**
 * Builds the standard analytics data object from profile and optional overrides.
 * Use this so every event sends the same base shape; callers add event-specific fields.
 */
export function buildAnalyticsData(
  profile: ProfileForAnalytics | null,
  overrides: AnalyticsEventData = {}
): AnalyticsEventData {
  if (!profile) {
    return overrides;
  }

  const profileInstitutionId = profile.relationships?.institution?.data?.id;
  const profileInstitutionName =
    profile.relationships?.institution?.attributes?.name ?? undefined;
  const base: AnalyticsEventData = {
    userId: profile.id,
  };
  if (profileInstitutionId) base.institutionId = profileInstitutionId;
  if (profileInstitutionName) base.institutionName = profileInstitutionName;

  const merged = { ...base, ...overrides };

  // When caller targets a different institution, do not inherit profile institutionName.
  const overrideInstitutionId = overrides.institutionId;
  if (
    overrideInstitutionId &&
    profileInstitutionId &&
    overrideInstitutionId !== profileInstitutionId &&
    overrides.institutionName === undefined
  ) {
    delete merged.institutionName;
  }

  return merged;
}

// ============================================================================
// Lead Analytics Metadata
// ============================================================================

export interface LeadAnalyticsMetadata {
  leadId: string;
  leadLifecycleStatus: 'archived' | 'active' | 'visitor';
  engagementLevel: string | null;
  leadSource: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  marketingConsent: string | null;
  profileData: {
    country: string;
    leadType: string;
    studentType: string;
    programs: string[];
    intakes: string[];
    programLevels: string[];
    campuses: string[];
    educationLevel: string | null;
    educationCountry: string | null;
    languageTestType: string | null;
  };
}

/**
 * Builds enriched lead metadata for analytics events.
 * Derives lifecycle status from lead state and extracts verification/consent info.
 */
export function buildLeadMetadata(lead: Lead): LeadAnalyticsMetadata {
  const { personal, preferences } = lead.profile;

  const emailVerified = lead.details.verifications.some(
    v => v.verifiedVia === 'email' && v.status === 'verified'
  );
  const phoneVerified = lead.details.verifications.some(
    v => v.verifiedVia === 'phoneNumber' && v.status === 'verified'
  );

  let leadLifecycleStatus: LeadAnalyticsMetadata['leadLifecycleStatus'] =
    'active';
  if (lead.isArchived) {
    leadLifecycleStatus = 'archived';
  } else if (!personal.email && !personal.phone) {
    leadLifecycleStatus = 'visitor';
  }

  return {
    leadId: lead.externalId,
    leadLifecycleStatus,
    engagementLevel: lead.engagementLevel ?? null,
    leadSource: lead.source,
    emailVerified,
    phoneVerified,
    marketingConsent: lead.details.marketingConsent?.status ?? null,
    profileData: {
      country: personal.country,
      leadType: personal.leadType,
      studentType: personal.studentType,
      programs: preferences.programs,
      intakes: preferences.intakes,
      programLevels: preferences.programLevels,
      campuses: preferences.campuses,
      educationLevel: lead.details.education?.educationLevel ?? null,
      educationCountry: lead.details.education?.educationCountry ?? null,
      languageTestType: lead.details.languages?.testType ?? null,
    },
  };
}
